import React, { type ReactNode, useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientResultType } from '@atlassian/search-client/common/types';
import { getJiraUrlWithAnalyticsQueryParams } from '@atlassian/search-common/analytics-query-params';
import { highlightMatchedText } from '@atlassian/search-common/highlight-matched-text';

import { ApplicationModes } from '../../../../../../common/constants/application-modes';
import { ProductKeys } from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { getJiraEntityIcon } from '../../../../../../common/ui/product-entity-icon';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import { BreadcrumbSocialSignalNext } from '../../../../../../common/ui/social-signals';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { getJiraSearchResultTypeMetadata } from '../../../../../../common/utils/get-jira-search-result-type-metadata';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { getSummarizedAdfContent } from '../../../../../../common/utils/summarized-adf-content';
import { useCrossAppLinkHandlers } from '../../../../../../common/utils/use-cross-app-link-handlers';
import { useSearchCrossProductUrlWrapper } from '../../../../../../common/utils/use-cross-product-url-wrapper';
import type { SharedEventAttributes } from '../../../../../../controllers/analytics/prepared-result-attributes';
import {
	ResultAnalytics,
	ResultCommonAttributes,
} from '../../../../../../controllers/analytics/result-analytics';
import type { ResultDerivedDataProps } from '../../../result-derived-data';
import type {
	searchJiraProject_AtlassianSearchPage$key,
	searchJiraProject_AtlassianSearchPage$data,
} from './__generated__/searchJiraProject_AtlassianSearchPage.graphql';
import { SearchJiraProjectTableRow } from './table-row';

export type SearchJiraProjectProps =
	SearchResultProps<searchJiraProject_AtlassianSearchPage$key> & {
		/**
		 * When true, render the Jira project table-row variant instead of the
		 * standard SearchResult card. Controlled exclusively by the parent list
		 * renderer (SearchResultsListLiteRenderer) — leaf renderers MUST NOT
		 * independently read feature flags to flip into the table form, otherwise
		 * the table row leaks onto the "All" tab whenever the lite-mode bootstrap
		 * flag is on.
		 */
		shouldRenderTableResult?: boolean;
	};

export const SearchJiraProject = (
	props: SearchJiraProjectProps & ResultDerivedDataProps,
): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchJiraProject_AtlassianSearchPage on SearchResultJiraProject
			@argumentDefinitions(includeJiraLiteModeFields: { type: "Boolean", defaultValue: false }) {
				__typename
				id
				title
				url
				type
				description
				projectKey
				lastModifiedDate
				iconUrl
				project {
					webUrl
					lead @include(if: $includeJiraLiteModeFields) {
						name
						picture
					}
					avatar @include(if: $includeJiraLiteModeFields) {
						small
					}
					projectStyle @include(if: $includeJiraLiteModeFields)
					projectType @include(if: $includeJiraLiteModeFields)
					category @include(if: $includeJiraLiteModeFields) {
						name
					}
				}
				socialSignal(tenantId: $tenantId) {
					signal
					timestamp
					user {
						accountId
						name
						picture
						... on AtlassianAccountUser {
							email
							extendedProfile {
								jobTitle
							}
						}
					}
				}
			}
		`,
		props.node,
	);

	return (
		<ResultCommonAttributes preparedResultAttributes={props.preparedResultAttributes}>
			{({ commonAttributes }) => (
				<SearchJiraProjectContent {...props} data={data} commonAttributes={commonAttributes} />
			)}
		</ResultCommonAttributes>
	);
};

const SearchJiraProjectContent = ({
	commonAttributes,
	preparedResultAttributes,
	intentDetectionTriggered,
	data,
	position,
	cloudId,
	searchQuery,
	searchSessionId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	mainContentWidth,
	selectedSites,
	productConfigs,
	shouldRenderTableResult = false,
}: SearchJiraProjectProps &
	ResultDerivedDataProps & {
		commonAttributes: SharedEventAttributes['commonAttributes'];
		data: searchJiraProject_AtlassianSearchPage$data;
	}): React.JSX.Element => {
	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
			...getSiteMetadata(data.id, cloudId, selectedSites),
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type, cloudId, selectedSites]);

	const url = useMemo(() => {
		const initialResultUrl = data.project?.webUrl || data.url;
		return getJiraUrlWithAnalyticsQueryParams({
			url: initialResultUrl,
			searchSessionId,
			resultId: data.id,
		});
	}, [data.project?.webUrl, data.url, data.id, searchSessionId]);

	const wrapCrossProductUrl = useSearchCrossProductUrlWrapper({
		bridge: 'searchPage',
		primaryProduct,
		primarySubProduct,
	});
	const tableRowUrl = useMemo(
		() => wrapCrossProductUrl(url, ProductKeys.Jira) ?? url,
		[wrapCrossProductUrl, url],
	);
	// Reports the cross-app interaction using the `xpis` param already on
	// `tableRowUrl`. Table rows bypass `SearchResult`, so this surface wires it
	// itself; a no-op when the URL was not wrapped.
	const crossAppLinkHandlers = useCrossAppLinkHandlers({
		href: tableRowUrl,
		destinationApp: ProductKeys.Jira,
	});

	const enrichedTitle = useMemo(() => {
		const prefix = data.projectKey;
		const { highlightedTitle } = highlightMatchedText(data.title, searchQuery.split(' '));

		if (prefix && !(highlightedTitle?.startsWith(prefix) || data.title.startsWith(prefix))) {
			return !highlightedTitle
				? prefix.concat(': ', data.title)
				: enrichExcerpt(prefix.concat(': ', highlightedTitle));
		}

		return !highlightedTitle ? data.title : enrichExcerpt(highlightedTitle);
	}, [data.title, data.projectKey, searchQuery]);

	const enrichedDescription = useMemo(() => {
		const reducedDescription = getSummarizedAdfContent(data.description, searchQuery);
		const { highlightedTitle } = highlightMatchedText(reducedDescription, searchQuery.split(' '));
		if (!highlightedTitle) {
			return reducedDescription;
		}
		return processExcerpt(highlightedTitle);
	}, [data.description, searchQuery]);

	const metadataParts: [string, ReactNode][] = useMemo(() => {
		const parts = [
			[
				'type',
				<SearchResultType
					type={getJiraSearchResultTypeMetadata(data.type as SearchClientResultType)}
				/>,
			],
			[
				'lastModified',
				data.lastModifiedDate ? <LastModified lastModified={data.lastModifiedDate} /> : null,
			],
		];
		if (
			appMode === ApplicationModes.Rovo &&
			data.socialSignal?.user?.email &&
			fg('search-plex-jira-social-signal')
		) {
			const { user, signal, timestamp } = data.socialSignal;
			parts.push([
				'social-signal',
				<BreadcrumbSocialSignalNext
					userAccountId={user.accountId}
					userName={user.name}
					userEmail={data.socialSignal?.user?.email}
					userPicture={user.picture}
					userJobTitle={user.extendedProfile?.jobTitle}
					signal={signal}
					timestamp={timestamp}
					commonAttributes={commonAttributes}
				/>,
			]);
		}
		return parts.filter(Boolean) as [string, ReactNode][];
	}, [data.socialSignal, data.type, data.lastModifiedDate, appMode, commonAttributes]);

	const icon = getJiraEntityIcon({
		productConfigs,
		iconUrl: data.iconUrl,
		type: data.type,
		isIssue: false,
		size: 'medium',
	});

	if (shouldRenderTableResult) {
		return (
			<ResultAnalytics
				preparedResultAttributes={preparedResultAttributes}
				intentDetectionTriggered={intentDetectionTriggered}
				metadata={metadata}
				position={position}
			>
				{({ onResultClicked, onResultContextMenu: onResultCtxMenuClicked }) => (
					<SearchJiraProjectTableRow
						productConfigs={productConfigs}
						iconUrl={data.project?.avatar?.small ?? ''}
						type={data.type}
						projectStyle={data.project?.projectStyle ?? ''}
						projectType={data.project?.projectType ?? ''}
						projectKey={data.projectKey}
						projectUrl={tableRowUrl}
						crossAppLinkHandlers={crossAppLinkHandlers}
						title={enrichedTitle}
						lead={
							data.project?.lead
								? { name: data.project.lead.name, picture: data.project.lead.picture }
								: null
						}
						category={data.project?.category?.name ?? ''}
						// TODO: properly wire up analytics
						onClick={() => onResultClicked({})}
						onContextMenu={() => onResultCtxMenuClicked({})}
					/>
				)}
			</ResultAnalytics>
		);
	}

	return (
		<ResultAnalytics
			preparedResultAttributes={preparedResultAttributes}
			intentDetectionTriggered={intentDetectionTriggered}
			metadata={metadata}
			position={position}
		>
			{({
				onResultClicked,
				onResultContextMenu: onResultCtxMenuClicked,
				onCopyLinkButtonClicked,
				onSummarizeInChatButtonClicked,
				onContextMenuCopyButtonClicked,
				onContextMenuButtonClicked,
			}) => (
				<SearchResult
					productConfigs={productConfigs}
					{...data}
					product={ProductKeys.Jira}
					productSelected={!!selectedProduct}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultCtxMenuClicked}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					url={url}
					icon={icon}
					title={enrichedTitle}
					headerMetadataParts={metadataParts}
					description={<DescriptionBody description={enrichedDescription} />}
					cloudId={cloudId}
					position={position}
					partial={data}
					appMode={appMode}
					mainContentWidth={mainContentWidth}
					selectedSites={selectedSites}
				/>
			)}
		</ResultAnalytics>
	);
};

export const isSearchJiraProject = (
	node?: any,
): node is searchJiraProject_AtlassianSearchPage$key => {
	return node?.__typename === 'SearchResultJiraProject';
};
